import { useTranslation } from "react-i18next";
import {
  driversListAsync,
  selectDriverUpdatedStatus,
  selectDriverNewStatus,
  driversAccessRequestsListAsync,
  driversSlice,
  selectDriversListStatus,
  selectDriverAccessRequestsListStatus,
} from "./driversSlice";
import { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { useNavigate } from "react-router-dom";
import { DriversSidePanel } from "./sidePanel/SidePanel";
import { selectCompany } from "../homeSlice";
import { supportPhonesByCompanyGroupUuid } from "../../../constants/support";
import { selectManagersList } from "../users/usersSlice";
import { SharedInterfaceDrivers } from "./sharedInterface";
import { triggerToast } from "@qivia/ui";
import { DriversListType } from "./driversAPI";
import {
  selectTripsByDayList,
  selectTripsByDayStatus,
  tripsAsync,
} from "../fleet/trips/tripsSlice";
import {
  advancedAnalyticVehiclesAsync,
  driversHistoryAsync,
  selectAdvancedAnalyticsVehiclesList,
  selectAdvancedAnalyticsVehiclesListStatus,
  selectDriversHistoryListStatus,
} from "../fleet/fleetSlice";
import {
  listAllVehiclesInDbAsync,
  selectListAllVehiclesInDbStatus,
} from "../vehicles/vehiclesSlice";

export const Drivers = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const driverUpdatedStatus = useAppSelector(selectDriverUpdatedStatus);
  const driversListStatus = useAppSelector(selectDriversListStatus);
  const tripsListInit = useAppSelector(selectTripsByDayList);
  const company = useAppSelector(selectCompany);
  const driverNewStatus = useAppSelector(selectDriverNewStatus);
  const managers = useAppSelector(selectManagersList);
  const vehiclesStatus = useAppSelector(selectListAllVehiclesInDbStatus);
  const driversHistoryListStatus = useAppSelector(
    selectDriversHistoryListStatus,
  );

  const advancedAnalyticsVehicleList = useAppSelector(
    selectAdvancedAnalyticsVehiclesList,
  );
  const advancedAnalyticsVehicleListStatus = useAppSelector(
    selectAdvancedAnalyticsVehiclesListStatus,
  );
  const driversAccessRequestsListStatus = useAppSelector(
    selectDriverAccessRequestsListStatus,
  );
  const tripsByDayStatus = useAppSelector(selectTripsByDayStatus);

  const otherSupportPhones = useMemo(
    () =>
      managers && managers.length > 0 && managers[0].companyGroup
        ? supportPhonesByCompanyGroupUuid[managers[0].companyGroup]
        : undefined,
    [managers],
  );

  const sidePanelAction = useCallback(
    (props: Partial<DriversListType>) => {
      navigate(`/home/drivers/${props.uuid}`);
    },
    [navigate],
  );

  useEffect(() => {
    if (company && driversListStatus === "idle") {
      void dispatch(driversListAsync(company.uuid));
    }
  }, [dispatch, company, driversListStatus]);

  useEffect(() => {
    if (company && driversAccessRequestsListStatus === "idle") {
      void dispatch(driversAccessRequestsListAsync(company.uuid));
    }
  }, [dispatch, company, driversAccessRequestsListStatus]);

  useEffect(() => {
    if (company && advancedAnalyticsVehicleListStatus === "idle") {
      void dispatch(advancedAnalyticVehiclesAsync(company.uuid));
    }
  }, [dispatch, company, advancedAnalyticsVehicleListStatus]);

  useEffect(() => {
    if (vehiclesStatus === "idle" && company) {
      void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
    }
  }, [dispatch, vehiclesStatus, company]);

  useEffect(() => {
    if (driversHistoryListStatus === "idle" && company) {
      void dispatch(driversHistoryAsync(company.uuid));
    }
  }, [dispatch, driversHistoryListStatus, company]);

  useEffect(() => {
    if (company?.hasAdvancedAnalytics && driversListStatus === "success")
      dispatch(
        driversSlice.actions.getEcoDrivingScoreAndDrivingEventsByDriver(
          tripsListInit,
        ),
      );
  }, [
    company?.hasAdvancedAnalytics,
    dispatch,
    driversListStatus,
    tripsListInit,
  ]);

  useEffect(() => {
    if (
      company &&
      company.hasAdvancedAnalytics &&
      advancedAnalyticsVehicleList &&
      advancedAnalyticsVehicleList.length > 0 &&
      advancedAnalyticsVehicleListStatus === "success" &&
      tripsByDayStatus === "idle"
    ) {
      void dispatch(
        tripsAsync({
          vehicles: advancedAnalyticsVehicleList,
        }),
      );
    }
  }, [
    dispatch,
    advancedAnalyticsVehicleListStatus,
    advancedAnalyticsVehicleList,
    tripsByDayStatus,
    company,
  ]);

  useEffect(() => {
    if (!driverNewStatus) return;
    if (driverUpdatedStatus === "success") {
      triggerToast(
        t(`drivers.updatedStatus.${driverNewStatus.toLowerCase()}.success`) ||
          "",
        "valid",
      );
    } else if (driverUpdatedStatus === "failed") {
      triggerToast(t("drivers.updatedStatus.failure") || "", "error");
    }
    dispatch(driversSlice.actions.resetDriverUpdatedStatus());
  }, [dispatch, driverNewStatus, driverUpdatedStatus, t]);

  return (
    <SharedInterfaceDrivers
      sidePanelAction={sidePanelAction}
      sidePanel={
        <DriversSidePanel
          canDeactivate
          canUpdateAnalyticCode
          canUpdateStatus
          hasAdvancedAnalytics={company?.hasAdvancedAnalytics ?? false}
        />
      }
      otherSupportPhones={otherSupportPhones}
    />
  );
};
